(() => {
  function withAddDeadline(operation) {
    const controller = new AbortController();
    let timer;
    const deadline = new Promise((_, reject) => {
      timer = window.setTimeout(() => {
        controller.abort();
        reject(new Error('Please check your bag to confirm the addition.'));
      }, 15000);
    });
    // Aborting the response cannot undo an accepted add. The caller must enter
    // bag review on timeout, never retry the item POST.
    return Promise.race([Promise.resolve().then(() => operation(controller.signal)), deadline])
      .finally(() => window.clearTimeout(timer));
  }

  function initialise(root) {
    if (root.dataset.mjReady) return;
    const data = root.querySelector('[data-mj-variants]');
    if (!data) return;
    const variants = JSON.parse(data.textContent);
    root.dataset.mjReady = 'true';
    const hero = root.querySelector('[data-mj-hero]');
    const thumbs = [...root.querySelectorAll('[data-mj-image]')];
    const select = root.querySelector('[data-mj-variant]');
    const optionSelects = [...root.querySelectorAll('[data-mj-option-index]')];
    const position = root.querySelector('[data-mj-position]');
    const arrows = root.querySelector('.mj-gallery__arrows');
    const galleryMapped = root.hasAttribute('data-mj-gallery-map');
    const price = root.querySelector('[data-mj-price]');
    const compare = root.querySelector('[data-mj-compare]');
    const mobilePrice = root.querySelector('[data-mj-mobile-price]');
    const mobileCompare = root.querySelector('[data-mj-mobile-compare]');
    const saleNotes = [...root.querySelectorAll('[data-mj-sale-note]')];
    const add = root.querySelector('[data-mj-add]');
    const status = root.querySelector('[data-mj-status]');
    const quantity = root.querySelector('[data-mj-quantity]');
    const quantityGroup = root.querySelector('.mj-quantity');
    const purchase = root.querySelector('.mj-purchase');
    const selectedImage = root.querySelector('[data-mj-selected-image]');
    const selectedTitle = root.querySelector('[data-mj-selected-title]');
    const bogo = root.querySelector('[data-mj-bogo]');
    const bogoToggle = root.querySelector('[data-mj-bogo-toggle]');
    const bogoFields = root.querySelector('[data-mj-bogo-fields]');
    const bogoSelect = root.querySelector('[data-mj-bogo-variant]');
    const bogoImage = root.querySelector('[data-mj-bogo-image]');
    const bogoFallback = root.querySelector('[data-mj-bogo-fallback]');
    const dock = root.querySelector('[data-mj-mobile-options]');
    const mobileIntro = root.querySelector('[data-mj-mobile-intro]');
    const dockLink = root.querySelector('[data-mj-options-link]');
    const dockTitle = root.querySelector('[data-mj-dock-title]');
    const dockPrice = root.querySelector('[data-mj-dock-price]');
    const form = root.querySelector('.mj-form');
    const dialog = root.querySelector('[data-mj-dialog]');
    const enlarged = root.querySelector('[data-mj-enlarged]');
    const zoom = root.querySelector('[data-mj-zoom]');
    const gallerySizes = '(min-width: 990px) 52vw, 100vw';
    const visibleThumbs = () => thumbs.filter(thumb => !thumb.hidden);
    const activeThumb = () => thumbs.find(thumb => thumb.dataset.mjImage === hero?.dataset.imageId);
    const selectedVariant = () => variants.find(variant => String(variant.id) === (select?.value || root.dataset.initialVariant));
    let displayedGalleryOption;
    let followPrimaryBirthstone = Boolean(bogoToggle?.defaultChecked);
    let purchaseState = 'idle';
    let purchaseGeneration = 0;
    const purchaseControls = [select, ...optionSelects, bogoToggle, bogoSelect, quantity].filter(Boolean);
    const lockedPurchaseControls = new Map();
    let warmGeneration = 0;
    const warmedImages = new Map();

    function warmAdjacent(thumb) {
      const generation = ++warmGeneration;
      const warm = () => {
        if (generation !== warmGeneration || !root.isConnected) return;
        const visible = visibleThumbs();
        const index = visible.indexOf(thumb);
        if (index < 0 || visible.length < 2) return;
        for (const offset of [-1, 1]) {
          const neighbour = visible[(index + offset + visible.length) % visible.length];
          if (neighbour === thumb || warmedImages.has(neighbour.dataset.mjImage)) continue;
          const image = new Image();
          image.fetchPriority = 'low';
          image.sizes = gallerySizes;
          if (neighbour.dataset.srcset) image.srcset = neighbour.dataset.srcset;
          image.src = neighbour.dataset.src;
          warmedImages.set(neighbour.dataset.mjImage, image);
        }
      };
      const schedule = () => {
        if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 1500 });
        else window.setTimeout(warm, 500);
      };
      if (document.readyState === 'complete') schedule();
      else window.addEventListener('load', schedule, { once: true });
    }

    function updatePurchaseState() {
      const busy = purchaseState === 'adding' || purchaseState === 'leaving';
      for (const control of purchaseControls) {
        if (busy) {
          if (!lockedPurchaseControls.has(control)) lockedPurchaseControls.set(control, control.disabled);
          control.disabled = true;
        } else if (lockedPurchaseControls.has(control)) {
          control.disabled = lockedPurchaseControls.get(control);
          lockedPurchaseControls.delete(control);
        }
      }
      form?.setAttribute('aria-busy', String(busy));
      const variant = selectedVariant();
      const second = variants.find(item => String(item.id) === bogoSelect?.value);
      if (bogoImage) {
        const secondThumb = thumbs.find(thumb => thumb.dataset.mjImage === String(second?.imageId));
        bogoImage.hidden = !second?.image;
        bogoImage.alt = second ? `${second.title} — free second necklace` : '';
        bogoImage.toggleAttribute('data-mj-studio', Boolean(secondThumb?.hasAttribute('data-mj-studio')));
        if (second?.image) {
          if (bogoImage.getAttribute('src') !== second.image) bogoImage.src = second.image;
        } else bogoImage.removeAttribute('src');
      }
      if (purchaseState !== 'idle') {
        const review = purchaseState === 'review';
        add.disabled = !review;
        add.textContent = review ? 'Check my bag' : purchaseState === 'adding' ? 'Adding to bag…' : 'Opening your bag…';
        if (dockLink) dockLink.textContent = add.textContent;
        status.textContent = review ? 'We couldn’t confirm whether your necklaces were added. Please check your bag before adding again.' : '';
        status.hidden = !review;
        return;
      }
      const pair = Boolean(bogoToggle?.checked);
      const available = Boolean(variant?.available) && (!pair || Boolean(second?.available));
      add.disabled = !available;
      add.textContent = !variant?.available ? 'Sold out' : pair && !second?.available ? 'Second month unavailable' : pair ? 'Add 2 necklaces to bag' : 'Add to bag';
      if (dockLink) dockLink.textContent = available ? pair ? 'Review pair' : 'Review & add' : 'Choose option';
      status.textContent = !variant?.available ? 'This option is currently unavailable.' : pair && !second?.available ? 'Choose an available second birth month.' : '';
      status.hidden = available;
    }

    function updatePosition() {
      const visible = visibleThumbs();
      const index = visible.indexOf(activeThumb());
      if (position) position.textContent = `${visible.length ? Math.max(index + 1, 1) : 0} / ${visible.length}`;
      if (arrows) arrows.hidden = visible.length < 2;
    }

    function showImage(thumb) {
      if (!hero || !thumb || thumb.hidden) return;
      const studio = thumb.hasAttribute('data-mj-studio');
      zoom?.toggleAttribute('data-mj-studio', studio);
      dialog?.toggleAttribute('data-mj-studio', studio);
      if (hero.dataset.imageId !== thumb.dataset.mjImage) {
        hero.sizes = gallerySizes;
        if (thumb.dataset.srcset) hero.srcset = thumb.dataset.srcset;
        else hero.removeAttribute('srcset');
        hero.src = thumb.dataset.src;
        hero.alt = thumb.dataset.alt;
        hero.width = Number(thumb.dataset.width);
        hero.height = Number(thumb.dataset.height);
        hero.dataset.imageId = thumb.dataset.mjImage;
      }
      thumbs.forEach(item => item.setAttribute('aria-pressed', String(item === thumb)));
      updatePosition();
      warmAdjacent(thumb);
      // Scroll only the thumbnail strip, keeping the purchase form in place.
      const strip = thumb.parentElement;
      if (thumb.offsetLeft < strip.scrollLeft || thumb.offsetLeft + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
        strip.scrollLeft = Math.max(0, thumb.offsetLeft - strip.clientWidth / 2 + thumb.offsetWidth / 2);
      }
    }

    thumbs.forEach(thumb => thumb.addEventListener('click', () => showImage(thumb)));
    function stepImage(step) {
      const visible = visibleThumbs();
      if (!visible.length) return;
      const current = visible.indexOf(activeThumb());
      showImage(visible[(current + step + visible.length) % visible.length]);
    }
    root.querySelector('[data-mj-previous]')?.addEventListener('click', () => stepImage(-1));
    root.querySelector('[data-mj-next]')?.addEventListener('click', () => stepImage(1));

    if (zoom) {
      let touchStart = null;
      let suppressZoomUntil = 0;
      zoom.addEventListener('touchstart', event => {
        // A new tap may open the image even if it follows a swipe immediately.
        suppressZoomUntil = 0;
        touchStart = event.touches.length === 1
          ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
          : null;
      }, { passive: true });
      zoom.addEventListener('touchend', event => {
        if (!touchStart || event.changedTouches.length !== 1) return;
        const dx = event.changedTouches[0].clientX - touchStart.x;
        const dy = event.changedTouches[0].clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
        suppressZoomUntil = Date.now() + 700;
        stepImage(dx < 0 ? 1 : -1);
        if (event.cancelable) event.preventDefault();
      }, { passive: false });
      zoom.addEventListener('touchcancel', () => { touchStart = null; }, { passive: true });
      zoom.addEventListener('click', event => {
        if (event.detail !== 0 && Date.now() < suppressZoomUntil) {
          event.preventDefault();
          suppressZoomUntil = 0;
          return;
        }
        const active = activeThumb();
        if (!active || !dialog?.showModal) return;
        enlarged.src = active.dataset.zoomSrc;
        enlarged.alt = active.dataset.alt;
        dialog.showModal();
      });
    }

    function updateVariant(variant, updateUrl = true, preferDefaultImage = false) {
      if (!variant) return;
      if (select) select.value = String(variant.id);
      optionSelects.forEach(control => {
        control.value = variant.options[Number(control.dataset.mjOptionIndex)];
      });
      const sale = Boolean(variant.sale);
      const displayedPrice = variant.sale || variant.price;
      const regularPrice = sale ? variant.price : variant.compare;
      price.textContent = displayedPrice;
      compare.textContent = regularPrice || '';
      compare.hidden = !regularPrice;
      price.closest('.mj-price')?.toggleAttribute('data-mj-christmas', sale);
      if (mobilePrice) mobilePrice.textContent = displayedPrice;
      if (mobileCompare) {
        mobileCompare.textContent = regularPrice || '';
        mobileCompare.hidden = !regularPrice;
        mobilePrice.closest('.mj-price')?.toggleAttribute('data-mj-christmas', sale);
      }
      saleNotes.forEach(note => { note.hidden = !sale; });
      if (selectedTitle) selectedTitle.textContent = `Selected: ${variant.title}`;
      if (dockTitle && select) dockTitle.textContent = variant.title;
      if (dockPrice) dockPrice.textContent = `${displayedPrice}${sale ? ' in bag' : ''}${variant.available ? '' : ' · Sold out'}`;
      // Keep the shopper's quantity when it fits the newly selected option's rules.
      const previousQuantity = Number(quantity.value);
      const minimum = Number(variant.min);
      const increment = Number(variant.step);
      let nextQuantity = Number.isFinite(previousQuantity) ? Math.max(minimum, previousQuantity) : minimum;
      if (variant.max != null) nextQuantity = Math.min(nextQuantity, Number(variant.max));
      nextQuantity = minimum + Math.floor((nextQuantity - minimum) / increment) * increment;
      quantity.min = variant.min;
      quantity.step = variant.step;
      quantity.value = Math.max(minimum, nextQuantity);
      if (variant.max != null) quantity.max = variant.max;
      else quantity.removeAttribute('max');
      if (bogoSelect && (!bogoToggle.checked || followPrimaryBirthstone)) bogoSelect.value = String(variant.id);
      const imageId = String(preferDefaultImage ? root.dataset.defaultImage : variant.imageId || root.dataset.defaultImage);
      const sameGalleryOption = galleryMapped && displayedGalleryOption === variant.optionValue;
      const priorThumb = activeThumb();
      thumbs.forEach(thumb => {
        if (galleryMapped) {
          const mappedOption = thumb.getAttribute('data-mj-gallery-option');
          thumb.hidden = mappedOption !== null && mappedOption !== variant.optionValue;
        } else {
          thumb.hidden = thumb.dataset.variantImage === 'true' && thumb.dataset.mjImage !== String(variant.imageId) && thumb.dataset.mjImage !== imageId;
        }
      });
      const featuredThumb = thumbs.find(thumb => thumb.dataset.mjImage === String(variant.imageId) && !thumb.hidden);
      const requestedThumb = thumbs.find(thumb => thumb.dataset.mjImage === imageId && !thumb.hidden);
      const nextThumb = preferDefaultImage
        ? requestedThumb || visibleThumbs()[0]
        : galleryMapped
          ? featuredThumb || (sameGalleryOption && priorThumb && !priorThumb.hidden ? priorThumb : visibleThumbs()[0])
          : requestedThumb || visibleThumbs()[0];
      displayedGalleryOption = variant.optionValue;
      if (zoom) zoom.hidden = galleryMapped && !nextThumb;
      if (nextThumb) showImage(nextThumb);
      else updatePosition();
      if (selectedImage) {
        const preview = galleryMapped
          ? (featuredThumb ? variant.image : nextThumb?.querySelector('img')?.src)
          : variant.image;
        const previewThumb = featuredThumb || (galleryMapped ? nextThumb : null);
        selectedImage.toggleAttribute('data-mj-studio', Boolean(previewThumb?.hasAttribute('data-mj-studio')));
        selectedImage.hidden = !preview;
        selectedImage.alt = variant.title;
        if (preview) selectedImage.src = preview;
        else selectedImage.removeAttribute('src');
      }
      updatePurchaseState();
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    select?.addEventListener('change', () => updateVariant(variants.find(variant => String(variant.id) === select.value)));
    optionSelects.forEach(control => control.addEventListener('change', () => {
      const chosen = optionSelects.map(item => item.value);
      const variant = variants.find(item => item.options?.every((value, index) => value === chosen[index]));
      if (variant) updateVariant(variant);
    }));
    function restoreLocation() {
      const id = new URL(window.location.href).searchParams.get('variant');
      const requestedVariant = variants.find(variant => String(variant.id) === id);
      const defaultVariant = variants.find(variant => String(variant.id) === (root.dataset.defaultVariant || root.dataset.initialVariant));
      updateVariant(requestedVariant || defaultVariant, false, !requestedVariant);
    }
    window.addEventListener('popstate', () => {
      if (root.isConnected) restoreLocation();
    });
    window.addEventListener('pageshow', event => {
      if (!event.persisted || !root.isConnected) return;
      // A request interrupted by navigation may already have changed the bag.
      // Ignore its late response and require review before another addition.
      purchaseGeneration += 1;
      if (purchaseState === 'adding') purchaseState = 'review';
      else if (purchaseState === 'leaving') purchaseState = 'idle';
      updatePurchaseState();
    });
    restoreLocation();

    function openBag() {
      purchaseState = 'leaving';
      updatePurchaseState();
      try {
        window.location.assign(root.dataset.mjCartUrl);
      } catch (error) {
        purchaseState = 'review';
        updatePurchaseState();
      }
    }

    if (bogo && bogoToggle && bogoFields && bogoSelect && root.dataset.mjCartAddUrl && root.dataset.mjCartUrl) {
      bogo.hidden = false;
      if (bogoFallback) bogoFallback.hidden = true;
      let singleQuantity = quantity.value;
      function updatePairSelection() {
        bogoFields.hidden = !bogoToggle.checked;
        followPrimaryBirthstone = bogoToggle.defaultChecked;
        if (bogoToggle.checked) {
          singleQuantity = quantity.value;
          quantity.value = '1';
          bogoSelect.value = select.value;
        } else {
          quantity.value = singleQuantity;
        }
        quantity.disabled = bogoToggle.checked;
        if (quantityGroup) quantityGroup.hidden = bogoToggle.checked;
        if (purchase) purchase.classList.toggle('mj-purchase--pair', bogoToggle.checked);
        if (bogoToggle.checked) updatePurchaseState();
        else updateVariant(selectedVariant(), false);
      }
      bogoToggle.addEventListener('change', updatePairSelection);
      bogoSelect.addEventListener('change', () => {
        followPrimaryBirthstone = false;
        updatePurchaseState();
      });
      if (bogoToggle.checked) updatePairSelection();
      quantity.addEventListener('input', updatePurchaseState);
      form.addEventListener('submit', async event => {
        // Guard before the single-necklace path, including keyboard submissions.
        if (purchaseState !== 'idle') {
          event.preventDefault();
          if (purchaseState === 'review') openBag();
          return;
        }
        if (!bogoToggle.checked) return;
        event.preventDefault();
        const first = selectedVariant();
        const second = variants.find(variant => String(variant.id) === bogoSelect.value);
        if (!first?.available || !second?.available) {
          status.textContent = 'Please choose two available birth months.';
          status.hidden = false;
          return;
        }
        const items = first.id === second.id
          ? [{ id: first.id, quantity: 2 }]
          : [{ id: first.id, quantity: 1 }, { id: second.id, quantity: 1 }];
        const generation = ++purchaseGeneration;
        purchaseState = 'adding';
        updatePurchaseState();
        try {
          await withAddDeadline(async signal => {
            const response = await fetch(root.dataset.mjCartAddUrl, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items }), signal,
            });
            if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
          });
          if (generation !== purchaseGeneration || !root.isConnected) return;
          openBag();
        } catch (error) {
          if (generation !== purchaseGeneration || !root.isConnected) return;
          // A lost response or even a Shopify stock error can follow a cart write.
          // Offer navigation to the bag instead of repeating an uncertain POST.
          purchaseState = 'review';
          updatePurchaseState();
        }
      });
    }

    if (dock && add) {
      const related = document.querySelector('.mj-related-section');
      const footer = document.querySelector('.mel-footer');
      const cutoffs = [related, footer].filter(Boolean);
      const media = window.matchMedia?.('(max-width: 749px)');
      const visibleFraction = element => {
        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return 0;
        const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
        const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        return visibleWidth * visibleHeight / (rect.width * rect.height);
      };
      const updateDockVisibility = () => {
        if (!root.isConnected) return;
        const beforePurchase = mobileIntro
          ? mobileIntro.getBoundingClientRect().bottom > 0
          : root.getBoundingClientRect().top >= 0;
        const pastCutoff = cutoffs.some(element => element.getBoundingClientRect().top <= window.innerHeight);
        dock.hidden = Boolean(media && !media.matches) || beforePurchase || visibleFraction(add) >= 0.75 || pastCutoff;
      };
      window.addEventListener('scroll', updateDockVisibility, { passive: true });
      window.addEventListener('resize', updateDockVisibility);
      window.addEventListener('pageshow', updateDockVisibility);
      window.addEventListener('popstate', updateDockVisibility);
      media?.addEventListener?.('change', updateDockVisibility);
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(updateDockVisibility, { threshold: [0, 0.75, 1] });
        [mobileIntro, add, ...cutoffs].filter(Boolean).forEach(element => observer.observe(element));
        document.addEventListener('shopify:section:unload', event => {
          if (event.target.contains(root)) observer.disconnect();
        });
      }
      updateDockVisibility();
    }
    dockLink?.addEventListener('click', event => {
      if (purchaseState !== 'idle') {
        event.preventDefault();
        if (purchaseState === 'review') openBag();
        return;
      }
      const pair = Boolean(bogoToggle?.checked);
      const unavailable = !selectedVariant()?.available;
      const primarySelect = optionSelects[0] || select;
      const control = unavailable ? primarySelect || quantity : pair ? bogoSelect || primarySelect || quantity : primarySelect || quantity;
      const target = unavailable ? primarySelect || form : pair ? bogo || form : primarySelect?.closest('.mj-field') || form;
      if (!target || !control) return;
      event.preventDefault();
      if (dock) dock.hidden = true;
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      if (event.detail === 0) control.focus({ preventScroll: true });
    });

    root.querySelector('[data-mj-close]')?.addEventListener('click', () => dialog.close());
    dialog?.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
  }

  const init = () => document.querySelectorAll('[data-mj-product]').forEach(initialise);
  init();
  document.addEventListener('shopify:section:load', init);
})();
